import { useQuery } from '@apollo/client';
import get from 'lodash/get';

import { gql } from '../gql-tag';
import {
  OperatorSessionQl,
  OperatorSessionModelFilter,
  OperatorSessionModelSortEnum,
} from '../graphql';

export const operatorFieldsFragment = gql(`
  fragment OperatorFields on OperatorQL {
    id
    name
    places {
      id
      name
    }
  }
`);

export const operatorPlaceFieldsFragment = gql(`
  fragment OperatorPlaceFields on OperatorPlaceQL {
    id
    operator {
      id
      name
    }
    place {
      id
      name
    }
  }
`);

export const operatorSessionFieldsFragment = gql(`
  fragment OperatorSessionFields on OperatorSessionQL {
    id
    createdOn
    callSid
    outcome
    slots
    phonecall {
      id
      callDuration
      isActive
      endtime
      startTime
      conversationId
      place {
        id
        name
      }
      returnCallsRoutingOutcome
      returnCallsRoutedTo
      returnCallsRoutedUser {
        id
        displayName
      }
      participant {
        id
        address
        displayName
      }
      callRecording {
        id
        url
      }
    }
  }
`);

export const GET_OPERATORS = gql(`
  query getOperators($accountId: String!) {
    operators(accountId: $accountId) {
      ...OperatorFields
    }
  }
`);

export const useGetOperators = (accountId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATORS, {
    variables: { accountId },
  });

  const operators = get(data, 'operators') ?? [];
  return { operators, loading, error };
};

export const GET_OPERATOR = gql(`
  query getOperator(
    $id: String!
    $includeChannels: Boolean = false
    $includeSessions: Boolean = false
  ) {
    operator(id: $id) {
      ...OperatorFields
      channels @include(if: $includeChannels) {
        id
        name
        ... on TwilioChannelQL {
          mdn
        }
        ... on BandwidthChannelQL {
          mdn
        }
      }
      operatorSessions @include(if: $includeSessions) {
        ...OperatorSessionFields
      }
    }
  }
`);

export const useGetOperatorById = (
  id: string,
  includeChannels: boolean = false,
  includeSessions: boolean = false,
) => {
  const { data, loading, error } = useQuery(GET_OPERATOR, {
    variables: { id, includeChannels, includeSessions },
  });

  const operator = get(data, 'operator');
  return { operator, loading, error };
};

export const GET_OPERATOR_PLACES = gql(`
  query getOperatorPlaces($operatorId: String!) {
    operatorPlaces(operatorId: $operatorId) {
      ...OperatorPlaceFields
    }
  }
`);

export const useGetOperatorPlaces = (operatorId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_PLACES, {
    variables: { operatorId },
  });

  const operatorPlaces = get(data, 'operatorPlaces') ?? [];
  return { operatorPlaces, loading, error };
};

export const GET_OPERATOR_PLACE = gql(`
  query getOperatorPlace($operatorId: String!, $placeId: String!) {
    operatorPlace(operatorId: $operatorId, placeId: $placeId) {
      ...OperatorPlaceFields
    }
  }
`);

export const useGetOperatorPlace = (operatorId: string, placeId: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_PLACE, {
    variables: { operatorId, placeId },
  });

  const operatorPlace = get(data, 'operatorPlace');
  return { operatorPlace, loading, error };
};

export const GET_OPERATOR_SESSIONS = gql(`
  query getOperatorSessions(
    $operatorId: String!
    $userIds: [String!]
    $placeIds: [String!]
    $first: Int
    $after: String
    $filter: OperatorSessionModelFilter
    $sort: [OperatorSessionModelSortEnum]
  ) {
    operatorSessions(
      operatorId: $operatorId
      userIds: $userIds
      placeIds: $placeIds
      first: $first
      after: $after
      queryFilter: $filter
      sort: $sort) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...OperatorSessionFields
        }
      }
      totalCount
    }
  }
`);

export const useGetOperatorSessions = ({
  operatorId,
  placeId,
  userId,
  first,
  after,
  filter = {},
  sort = [OperatorSessionModelSortEnum.CreatedOnDesc],
}: {
  operatorId: string;
  placeId?: string;
  userId?: string;
  first?: number;
  after?: string | null;
  filter?: OperatorSessionModelFilter;
  sort?: OperatorSessionModelSortEnum[];
}) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_SESSIONS, {
    variables: {
      operatorId,
      placeIds: placeId ? [placeId] : [],
      userIds: userId ? [userId] : [],
      filter,
      first,
      after,
      sort,
    },
  });

  const totalCount = get(data, 'operatorSessions.totalCount');
  const endCursor = get(data, 'operatorSessions.pageInfo.endCursor');
  const hasNextPage = get(data, 'operatorSessions.pageInfo.hasNextPage');
  const operatorSessions = (get(data, 'operatorSessions.edges') ?? [])
    .map(edge => {
      if (!edge || !edge.node) {
        return null;
      }

      return edge.node as OperatorSessionQl;
    })
    .filter((node): node is OperatorSessionQl => node !== null);

  return {
    operatorSessions,
    loading,
    error,
    totalCount,
    endCursor,
    hasNextPage,
  };
};

export const GET_OPERATOR_SESSION = gql(`
  query getOperatorSession($id: String!) {
    operatorSession(id: $id) {
      ...OperatorSessionFields
    }
  }
`);

export const useGetOperatorSessionById = (id: string) => {
  const { data, loading, error } = useQuery(GET_OPERATOR_SESSION, {
    variables: { id },
  });

  const operatorSession = get(data, 'operatorSession');
  return { operatorSession, loading, error };
};
